import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import {
  ShopAntifraudConfig,
  ShopAntifraudConfigService,
  ShopAntifraudLimitOrders
} from 'src/app/services/shop-antifraud-config.service';

type LimitOrdersGroup = FormGroup<{
  cpf: FormControl<number | null>;
  email: FormControl<number | null>;
  phone: FormControl<number | null>;
  zip: FormControl<number | null>;
  card: FormControl<number | null>;
}>;

@Component({
  selector: 'app-shop-antifraud-limits',
  templateUrl: './shop-antifraud-limits.component.html',
  styleUrl: './shop-antifraud-limits.component.scss',
  providers: [MessageService]
})
export class ShopAntifraudLimitsComponent implements OnInit {
  private title = inject(Title);
  private fb = inject(FormBuilder);
  private shopAntifraudConfigService = inject(ShopAntifraudConfigService);
  private messageService = inject(MessageService);

  saving = false;
  source: 'config' | 'missing' | 'invalid' | null = null;
  shopConfigsId: string | null = null;
  limitFields: Array<keyof ShopAntifraudLimitOrders> = [
    'cpf',
    'email',
    'phone',
    'zip',
    'card'
  ];

  /** Texto humano do status da fonte (sem id técnico do Firestore). */
  get sourceStatus(): string | null {
    switch (this.source) {
      case 'config':
        return 'Status: configuração ativa — o checkout usa os valores desta tela.';
      case 'missing':
        return 'Status: nenhuma configuração salva — o checkout está nos defaults.';
      case 'invalid':
        return 'Status: configuração inválida — o checkout está nos defaults.';
      default:
        return null;
    }
  }

  form = this.fb.group({
    enabled: this.fb.nonNullable.control(true),
    reason: this.fb.nonNullable.control('', [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(500)
    ]),
    day: this.fb.group({
      subscriber: this.createLimitGroup(false),
      nonSubscriber: this.createLimitGroup(false)
    }),
    week: this.fb.group({
      subscriber: this.createLimitGroup(true),
      nonSubscriber: this.createLimitGroup(true)
    }),
    blockedAttemptsDay: this.fb.group({
      subscriber: this.fb.control<number | null>(7, [
        Validators.required,
        Validators.min(1),
        Validators.max(50)
      ]),
      nonSubscriber: this.fb.control<number | null>(5, [
        Validators.required,
        Validators.min(1),
        Validators.max(50)
      ])
    })
  });

  async ngOnInit() {
    this.title.setTitle('Limites antifraude loja | Checkout cartão');
    await this.load();
  }

  async load() {
    LoaderService.showLoader();
    try {
      const result = await lastValueFrom(
        this.shopAntifraudConfigService.getConfig()
      );
      this.shopConfigsId = result.id ?? null;
      this.source = result.source ?? null;
      const defaults = result.fallbackDefaults ?? this.defaultConfig();
      // config válida → payload; invalid/missing → defaults (preserva kill-switch se vier enabled:false)
      const config =
        result.source === 'config' && result.antifraude
          ? result.antifraude
          : {
              ...defaults,
              enabled:
                result.antifraude?.enabled === false ? false : defaults.enabled
            };
      this.patchForm(config);
      this.form.controls.reason.reset('');
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Preencha todos os campos obrigatórios (inclui o motivo).'
      });
      return;
    }

    this.saving = true;
    LoaderService.showLoader();
    try {
      const value = this.form.getRawValue();
      const antifraude = this.toAntifraudPayload(value);
      const result = await lastValueFrom(
        this.shopAntifraudConfigService.updateConfig(
          antifraude,
          value.reason.trim()
        )
      );
      this.source = 'config';
      this.shopConfigsId = result.id ?? this.shopConfigsId;
      this.form.controls.reason.reset('');
      const auditOk = result.auditPersisted !== false;
      this.messageService.add({
        severity: auditOk ? 'success' : 'warn',
        summary: auditOk ? 'Sucesso' : 'Salvo com aviso',
        detail: auditOk
          ? 'Limites antifraude atualizados. Checkout aplica em ~60s.'
          : 'Limites salvos na loja, mas a auditoria SQL falhou — avise IT.'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      this.saving = false;
      LoaderService.showLoader(false);
    }
  }

  fieldLabel(field: keyof ShopAntifraudLimitOrders): string {
    const labels: Record<keyof ShopAntifraudLimitOrders, string> = {
      cpf: 'CPF',
      email: 'E-mail',
      phone: 'Telefone',
      zip: 'CEP',
      card: 'Cartão'
    };
    return labels[field];
  }

  private createLimitGroup(allowNullZip: boolean): LimitOrdersGroup {
    return this.fb.group({
      cpf: this.fb.control<number | null>(null, [
        Validators.required,
        Validators.min(1),
        Validators.max(allowNullZip ? 200 : 50)
      ]),
      email: this.fb.control<number | null>(null, [
        Validators.required,
        Validators.min(1),
        Validators.max(allowNullZip ? 200 : 50)
      ]),
      phone: this.fb.control<number | null>(null, [
        Validators.required,
        Validators.min(1),
        Validators.max(allowNullZip ? 200 : 50)
      ]),
      zip: this.fb.control<number | null>(
        null,
        allowNullZip
          ? [Validators.min(1), Validators.max(200)]
          : [Validators.required, Validators.min(1), Validators.max(50)]
      ),
      card: this.fb.control<number | null>(null, [
        Validators.required,
        Validators.min(1),
        Validators.max(allowNullZip ? 200 : 50)
      ])
    });
  }

  private patchForm(config: ShopAntifraudConfig) {
    this.form.patchValue({
      enabled: config.enabled,
      day: config.day,
      week: {
        subscriber: {
          ...config.week.subscriber,
          zip: config.week.subscriber.zip ?? null
        },
        nonSubscriber: {
          ...config.week.nonSubscriber,
          zip: config.week.nonSubscriber.zip ?? null
        }
      },
      blockedAttemptsDay: config.blockedAttemptsDay
    });
  }

  private toAntifraudPayload(value: {
    enabled: boolean;
    reason: string;
    day: {
      subscriber: ReturnType<LimitOrdersGroup['getRawValue']>;
      nonSubscriber: ReturnType<LimitOrdersGroup['getRawValue']>;
    };
    week: {
      subscriber: ReturnType<LimitOrdersGroup['getRawValue']>;
      nonSubscriber: ReturnType<LimitOrdersGroup['getRawValue']>;
    };
    blockedAttemptsDay: {
      subscriber: number | null;
      nonSubscriber: number | null;
    };
  }): ShopAntifraudConfig {
    return {
      enabled: value.enabled,
      day: {
        subscriber: this.requiredLimits(value.day.subscriber),
        nonSubscriber: this.requiredLimits(value.day.nonSubscriber)
      },
      week: {
        subscriber: this.weekLimits(value.week.subscriber),
        nonSubscriber: this.weekLimits(value.week.nonSubscriber)
      },
      blockedAttemptsDay: {
        subscriber: value.blockedAttemptsDay.subscriber as number,
        nonSubscriber: value.blockedAttemptsDay.nonSubscriber as number
      }
    };
  }

  private requiredLimits(
    limits: ReturnType<LimitOrdersGroup['getRawValue']>
  ): ShopAntifraudLimitOrders {
    return {
      cpf: limits.cpf as number,
      email: limits.email as number,
      phone: limits.phone as number,
      zip: limits.zip as number,
      card: limits.card as number
    };
  }

  private weekLimits(
    limits: ReturnType<LimitOrdersGroup['getRawValue']>
  ): ShopAntifraudLimitOrders {
    return {
      cpf: limits.cpf as number,
      email: limits.email as number,
      phone: limits.phone as number,
      zip: limits.zip == null ? null : limits.zip,
      card: limits.card as number
    };
  }

  private defaultConfig(): ShopAntifraudConfig {
    return {
      enabled: true,
      day: {
        subscriber: { cpf: 4, email: 4, phone: 4, card: 4, zip: 4 },
        nonSubscriber: { cpf: 2, email: 2, phone: 2, card: 2, zip: 2 }
      },
      week: {
        subscriber: { cpf: 12, email: 12, phone: 12, card: 12, zip: null },
        nonSubscriber: { cpf: 7, email: 7, phone: 7, card: 7, zip: null }
      },
      blockedAttemptsDay: { subscriber: 7, nonSubscriber: 5 }
    };
  }
}
